import React from "react";
import { Typography } from "@mui/material";

const UsingToolGuide = () => {
  return (
    <>
      <Typography variant="h6" className="section-heading" color="secondary">
        Getting Started
      </Typography>
      <Typography variant="body1">
        Loops & Purls is a web-based chart maker that lets you create knitting patterns visually. No
        account required—just open the Chart Maker and start designing!
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Creating Your Chart
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Set Your Grid Size</span>
          <span className="feature-item-description">
            Use the grid size selector to choose dimensions up to 50×50 stitches. Start small for
            simple patterns, or go large for complex designs.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Select a Stitch Symbol (Brush)</span>
          <span className="feature-item-description">
            Click any symbol in the stitch palette to select it as your brush. The selected symbol
            will be applied when you click or drag on the grid.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Paint Your Pattern</span>
          <span className="feature-item-description">
            Click individual cells to place stitches, or click and drag to paint multiple cells at
            once. This makes it easy to fill large areas quickly.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Navigate with Keyboard</span>
          <span className="feature-item-description">
            Use Tab or arrow keys to move between cells. This is especially helpful for precise
            placement or when working row by row.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Zoom Controls</span>
          <span className="feature-item-description">
            For large grids (20×20+), use the zoom controls to get a better view. Zoom in for
            detail work, zoom out to see the big picture, or use 'Fit' to automatically adjust.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Stitch Palette
      </Typography>
      <Typography variant="body1">
        The stitch palette displays all available stitch symbols, organized by category. Click any
        symbol to select it as your brush, then use it to paint your pattern on the grid.
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Basics</span>
          <span className="feature-item-description">
            Knit (◊), Purl (●), and No Stitch (⧄) symbols for fundamental stitches
          </span>
        </li>
        <li>
          <span className="feature-item-title">Increases</span>
          <span className="feature-item-description">
            Yarn Over (○), Make 1 Left/Right (◐, ◑), and Knit Front & Back (↱) for adding stitches
          </span>
        </li>
        <li>
          <span className="feature-item-title">Decreases</span>
          <span className="feature-item-description">
            K2Tog (◢), SSK (◣), P2Tog (◥), and other decrease symbols for removing stitches
          </span>
        </li>
        <li>
          <span className="feature-item-title">Cables</span>
          <span className="feature-item-description">
            Cable symbols (◄, ►) for left and right twists in various sizes (2, 3, or 4 stitches)
          </span>
        </li>
        <li>
          <span className="feature-item-title">Lace</span>
          <span className="feature-item-description">
            Yarn over combinations (◇, ◆) for creating decorative eyelets and lace patterns
          </span>
        </li>
        <li>
          <span className="feature-item-title">Texture</span>
          <span className="feature-item-description">
            Slip stitches (◈), bobbles (◘), and nupps (◙) for adding texture and dimension
          </span>
        </li>
      </ul>
      <Typography variant="body2" color="text.secondary">
        All symbols follow Craft Yarn Council international standards. See the "Stitch Guide"
        section below for complete details on every symbol, including abbreviations and descriptions.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Grid Settings
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Flat vs. In the Round</span>
          <span className="feature-item-description">
            Choose whether your pattern is worked flat (back and forth) or in the round. This
            affects how rows are numbered and displayed.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Row and Column Numbers</span>
          <span className="feature-item-description">
            Toggle row and column numbers on or off. These help you track your position in the
            pattern.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Exporting Your Pattern
      </Typography>
      <Typography variant="body1">
        Click the "Export" button to save your pattern. You have two format options:
      </Typography>

      <Typography variant="subtitle1" className="section-heading" fontWeight="medium">
        PDF Export
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Page Orientation</span>
          <span className="feature-item-description">
            Choose Portrait for tall charts or Landscape for wide charts. Landscape works well for
            shawls and wide patterns.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Include Content</span>
          <span className="feature-item-description">
            Select what to include: Pattern Title, Chart Grid, Written Instructions, and Stitch
            Key. You can mix and match based on your needs.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Stitch Key Options</span>
          <span className="feature-item-description">
            Include a stitch key showing all symbols used. Choose abbreviated names (K, P, K2Tog) or
            full names (Knit, Purl, Knit 2 Together). Optionally include descriptions for each
            stitch.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Export Quality</span>
          <span className="feature-item-description">
            Choose from Low (3x), Medium (4x), High (6x), or Ultra (8x) quality. Higher quality
            produces larger files but better print resolution. Use High or Ultra for large charts
            you plan to print.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Large Charts</span>
          <span className="feature-item-description">
            Charts larger than 50×50 stitches are automatically split into multiple PDF pages for
            optimal printing quality. Each section shows a 50×50 stitch area.
          </span>
        </li>
      </ul>

      <Typography variant="subtitle1" className="section-heading" fontWeight="medium">
        JSON Export
      </Typography>
      <Typography variant="body1">
        Export your pattern as a JSON file to save your work and reload it later. This preserves
        your exact chart data, including all stitch placements.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        To import a previously saved pattern, use the "Import Pattern" option in the Export modal.
        Select your JSON file to restore your chart exactly as you left it.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Tips & Tricks
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Save Frequently</span>
          <span className="feature-item-description">
            Use JSON export to save your work regularly. This way, you won't lose progress if you
            need to close your browser.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Use Clear Grid</span>
          <span className="feature-item-description">
            The 'Clear Grid' button resets your entire chart. Use it when starting a new design or
            making major changes.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Written Instructions</span>
          <span className="feature-item-description">
            Written instructions are generated automatically as you work. They update in real-time,
            showing row-by-row instructions based on your chart.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Pattern Title</span>
          <span className="feature-item-description">
            Always add a title to your pattern before exporting. This makes it easier to identify
            your files later.
          </span>
        </li>
      </ul>
    </>
  );
};

export default UsingToolGuide;

